import { useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../NotifyProvider';
import { createPrintActionKeys } from '../../utils/printClient';
import { fulfillmentApiBase, serviceLabelPdfPath } from '../../utils/fulfillmentApi.mjs';
import { openOrDownloadPdfBlob } from '../../pages/fulfillmentUi.mjs';

const NO_PRINTER_MSG = 'Выберите принтер в Настройки → Печать';

async function blobErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch {
      /* не JSON */
    }
  }
  return data?.message || error?.message || fallback;
}

/**
 * Служебная наклейка «Подтвердить» (PKR-CONFIRM) для стола проклейки.
 * variant="icon" — значок печати на ТСД (только печать на выбранный принтер);
 * variant="buttons" — кнопки в АРМ: печать и PDF для офисного принтера.
 */
export default function ServiceLabelActions({ printerUid = '', variant = 'icon', disabled = false }) {
  const { success: notifySuccess, error: notifyError } = useNotify();
  const [busy, setBusy] = useState('');
  const printKeysRef = useRef(null);
  if (!printKeysRef.current) printKeysRef.current = createPrintActionKeys();

  const print = async () => {
    if (!printerUid) {
      notifyError(NO_PRINTER_MSG);
      return;
    }
    setBusy('print');
    try {
      await printKeysRef.current.run('service-label', printerUid, (key) => axios.post(
        `${fulfillmentApiBase()}/labeling/service-labels/print`,
        { printer_uid: printerUid, code: 'confirm', copies: 1 },
        { headers: { 'Idempotency-Key': key }, silent: true },
      ));
      notifySuccess('ШК «Подтвердить» отправлен на печать');
    } catch (requestError) {
      notifyError(await blobErrorMessage(requestError, 'Не удалось отправить на печать'));
    } finally {
      setBusy('');
    }
  };

  const download = async () => {
    setBusy('pdf');
    try {
      const { data } = await axios.get(serviceLabelPdfPath('confirm'), { responseType: 'blob', silent: true });
      if (String(data?.type || '').includes('application/json')) {
        const parsed = JSON.parse(await data.text());
        throw new Error(parsed?.message || 'Не удалось получить PDF');
      }
      openOrDownloadPdfBlob(data, 'shk-podtverdit.pdf');
    } catch (requestError) {
      notifyError(await blobErrorMessage(requestError, 'Не удалось получить PDF'));
    } finally {
      setBusy('');
    }
  };

  if (variant === 'buttons') {
    return (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Tooltip title={printerUid ? 'Наклейка на стол проклейки' : NO_PRINTER_MSG}>
          <span>
            <Button
              variant="outlined"
              startIcon={busy === 'print' ? <CircularProgress size={16} /> : <PrintIcon />}
              onClick={print}
              disabled={disabled || Boolean(busy) || !printerUid}
            >
              Напечатать ШК «Подтвердить»
            </Button>
          </span>
        </Tooltip>
        <Button
          startIcon={busy === 'pdf' ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={download}
          disabled={disabled || Boolean(busy)}
        >
          Скачать PDF
        </Button>
      </Stack>
    );
  }

  return (
    <Tooltip title="Напечатать ШК «Подтвердить»">
      <span>
        <IconButton
          aria-label="Напечатать ШК «Подтвердить»"
          onClick={print}
          disabled={disabled || Boolean(busy)}
          color={printerUid ? 'primary' : 'default'}
        >
          {busy ? <CircularProgress size={20} /> : <PrintIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
